// pkceUtils.js
import { getItem, setItem, removeItem } from "./storageHelper";
/**
 * Utility functions for implementing PKCE (Proof Key for Code Exchange)
 * Used in OAuth 2.0 authorization code flow with PKCE
 */

/**
 * Generates a random code verifier for PKCE
 * @returns {string} A URL-safe base64 encoded random string
 */
export function generateCodeVerifier() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

/**
 * Generates a code challenge from a code verifier using SHA-256
 * @param {string} codeVerifier - The code verifier to generate the challenge from
 * @returns {Promise<string>} A URL-safe base64 encoded SHA-256 hash of the code verifier
 */
export async function generateCodeChallenge(codeVerifier) {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

/**
 * Stores the code verifier in localStorage
 * @param {string} verifier - The code verifier to store
 */
export async function storeCodeVerifier(verifier) {
    await setItem('code_verifier', verifier);
}

/**
 * Retrieves the stored code verifier from localStorage
 * @returns {string|null} The stored code verifier or null if not found
 */
export async function getStoredCodeVerifier() {
    return await getItem('code_verifier');
}

/**
 * Clears the stored code verifier from localStorage
 */
export function clearCodeVerifier() {
    removeItem('code_verifier');
}

/**
 * Creates a complete PKCE challenge pair
 * @returns {Promise<{verifier: string, challenge: string}>} Object containing both the verifier and challenge
 */
export async function createPkceChallengePair() {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    return { verifier, challenge };
}